// src/data/claimDocumentClient.js
//
// SECTION 412 PART A + B - THE CLIENT SIDE OF THE MEDIATED CLAIM-DOCUMENT UPLOAD.
//
// ONE place that attaches a file to a claim, used by ClaimDocuments.js (officers, after the claim
// exists), QuickFile Box 5 and NewClaim (the member, at the exhibits step). Client writes to
// claims/{claimId}/documents are OFF in storage.rules; the ONLY way a file lands is:
//   1. call claimDocumentUploadUrl - the callable verifies the caller is a party to THIS claim and
//      returns a resumable upload session whose metadata it baked in server-side;
//   2. PUT the bytes to that session URI (up to 25MB, which is why they do not travel through the call);
//   3. return the claim.documents entry, carrying the object's storagePath. Record 591 (ruling 247): the
//      download URL is NOT read here - the file is unreadable until the scanner marks it clean - so it is
//      fetched when View is clicked (src/data/scannedFile.js). downloadURL stays on the entry, empty.
//
// The version MARK (src/data/documentVersion.js) is assigned exactly as ClaimDocuments always did it,
// so the two surfaces cannot drift (SS XI). This module is NOT projected to functions/ - it is browser
// code (firebase, XHR); the pure decision it shares with the callable lives in claimDocumentUpload.js.
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../firebase';
import { markFor, existingIn, newLineageKey } from './documentVersion';
import { validateClaimUpload } from './claimDocumentUpload';

// PUT the whole file to the resumable session URI in one request. The Google resumable protocol
// finalises a session on a single PUT that carries the entire body; for a <=25MB exhibit that is one
// request, and XHR gives us the upload progress the UI shows. Proved end-to-end on the Storage
// emulator by scripts/test-claim-upload-session.mjs.
function putToSession(uploadUrl, file, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('Content-Type', contentType || file.type || 'application/octet-stream');
    if (xhr.upload && typeof onProgress === 'function') {
      xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)); };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error('the upload was refused (' + xhr.status + ')'));
    };
    xhr.onerror = () => reject(new Error('the upload could not reach storage'));
    xhr.send(file);
  });
}

// Attach one file to a claim. Returns the entry to add to claim.documents (the caller persists it, so
// this works whether the claim already exists or was just created in the same submit flow).
//
//   { claimId, file, docType, description, documents, versionOf, versionRole, versionReason,
//     mediaOrigin, user, onProgress }
export async function uploadClaimDocument(opts) {
  const {
    claimId, file, docType = 'Exhibit', description = '',
    documents = [], versionOf = '', versionRole = null, versionReason = '',
    mediaOrigin, user, onProgress,
  } = opts || {};

  if (!claimId) throw new Error('This claim has no id yet, so a file cannot be attached to it.');
  if (!file) throw new Error('No file was provided.');

  // Same validation the callable runs, run here first so the member is told at the field.
  const v = validateClaimUpload({ contentType: file.type, bytes: file.size, mediaOrigin });
  if (!v.ok) throw new Error(v.why);

  // THE MARK. Assigned by documentVersion.js, never here - one live copy per lineage, monotonic
  // numbering, refused duplicates. A new attachment (no versionOf) starts its own lineage as the
  // original; a revision joins the named lineage. Same rule ClaimDocuments always used.
  const lineageKey = versionOf || newLineageKey();
  const mark = markFor({
    existing: versionOf ? existingIn(documents, versionOf) : [],
    role: versionOf ? versionRole : (versionRole === 'final' ? 'final' : (versionRole === 'live' ? 'live' : null)),
    reason: versionReason,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  if (!mark.ok) throw new Error(mark.errors.join(' '));

  // 1. The callable: membership verified server-side, a session URI baked with the claim's metadata.
  const fn = httpsCallable(getFunctions(), 'claimDocumentUploadUrl');
  const res = await fn({
    claimId,
    file: { name: file.name, contentType: file.type || 'application/octet-stream', bytes: file.size, mediaOrigin },
    origin: (typeof window !== 'undefined' && window.location && window.location.origin) || undefined,
  });
  const out = (res && res.data) || {};
  if (!out.ok || !out.uploadUrl || !out.storagePath) throw new Error('The upload could not be authorised.');

  // 2. The bytes.
  await putToSession(out.uploadUrl, file, out.contentType, onProgress);

  // 3. Record 591 (ruling 247): no download URL. Asking for one now is refused (the file is not scanned yet),
  //    and this line used to swallow that refusal and save '' - a View link that never worked. The entry
  //    carries storagePath; View fetches the link when clicked, after the scan.
  const downloadURL = '';

  const who = (user && (user.name || user.email)) || (auth.currentUser && auth.currentUser.email) || 'Unknown';
  return {
    name: file.name,
    type: docType,
    description: description || '',
    fileType: file.type || '',
    fileSize: file.size,
    storagePath: out.storagePath,
    downloadURL,
    uploadedBy: who,
    uploadedAt: new Date().toISOString(),
    lineageKey,
    versionCode: mark.code,
    versionSequence: mark.sequence,
    versionWatermark: mark.watermark,
    versionMarkedAt: new Date().toISOString(),
  };
}

// SECTION 412 PART B - attach the exhibit files a member staged at filing time, AFTER the claim exists
// (its id is needed before its documents can be attached - the callable reads the claim to verify
// membership). Used by QuickFile Box 5 and NewClaim. Each file is a new exhibit lineage (marked as the
// original). A per-file failure does NOT lose the others or the claim: it is returned in `errors` so the
// surface can tell the member which ones to attach later from the claim, exactly as ClaimDocuments does.
//   { claimId, exhibitFiles: [{ file, description }], user, onProgress } -> { entries, errors }
export async function attachStagedExhibits(opts) {
  const { claimId, exhibitFiles = [], user, onProgress } = opts || {};
  const entries = [];
  const errors = [];
  for (let i = 0; i < exhibitFiles.length; i++) {
    const it = exhibitFiles[i] || {};
    if (!it.file) continue;
    try {
      const entry = await uploadClaimDocument({
        claimId, file: it.file, docType: 'Exhibit',
        description: (it.description && it.description.trim()) || it.file.name,
        user,
        onProgress: (pct) => { if (typeof onProgress === 'function') onProgress(Math.round(((i / exhibitFiles.length) + (pct / 100 / exhibitFiles.length)) * 100)); },
      });
      entries.push(entry);
    } catch (e) {
      errors.push((it.file.name || 'a file') + ': ' + ((e && e.message) || String(e)));
    }
  }
  return { entries, errors };
}
